import { FC, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';

const COPIED_FOR_MS = 1500;

/** Puts text on the clipboard. Falls back to a hidden textarea where the async clipboard API is not available (plain http, old browsers). */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // denied or unavailable - try the fallback below
  }

  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(field);
    return copied;
  } catch {
    return false;
  }
};

interface AssistantCopyButtonProps {
  text: string;
  label: string;
  copiedLabel: string;
  className?: string;
}

/** An icon button that copies `text`, and shows a check mark for a moment to say it worked. */
const AssistantCopyButton: FC<AssistantCopyButtonProps> = ({ text, label, copiedLabel, className }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const onClick = async () => {
    if (!(await copyToClipboard(text))) {
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_FOR_MS);
  };

  return (
    <button
      type="button"
      className={'assistant-copy-btn' + (copied ? ' is-copied' : '') + (className ? ' ' + className : '')}
      title={copied ? copiedLabel : label}
      aria-label={copied ? copiedLabel : label}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
    </button>
  );
};

export default AssistantCopyButton;
